import { Download, Pause, Play, RotateCcw } from "lucide-react";
import StemRow from "@/components/creator/stems/StemRow";
import useStemPlayer from "@/components/creator/stems/useStemPlayer";
import { fmtBytes, fmtClock, stemsZipUrl } from "@/lib/studioApi";

/** Listen to the split parts together, balance them, and download one part or all of them. */
export default function StemMixer({ set }) {
  const p = useStemPlayer(set.id, set.stems);
  const anySolo = Object.values(p.mix).some((m) => m.solo);
  const pct = p.duration ? Math.min(100, (p.time / p.duration) * 100) : 0;
  const total = (k) => set.stems.reduce((n, s) => n + (s[k] || 0), 0);
  const onSeek = (e) => {
    if (!p.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    p.seek(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * p.duration);
  };
  return (
    <section data-testid="stem-mixer" className="space-y-4">
      <div className="flex flex-wrap items-center gap-4 rounded-3xl border border-cyan-400/20 bg-cyan-500/[0.05] p-4">
        <button type="button" data-testid="stems-play" onClick={p.playing ? p.pause : p.play} aria-label={p.playing ? "Pause" : "Play all parts"}
          className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-[#05070f] transition-transform duration-200 hover:scale-105">
          {p.playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
        </button>
        <div className="min-w-[200px] flex-1">
          <div role="slider" tabIndex={0} aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(p.duration)} aria-valuenow={Math.round(p.time)}
            data-testid="stems-seek" onClick={onSeek} className="relative h-2 cursor-pointer rounded-full bg-white/10">
            <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cyan-300 to-blue-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-1 flex justify-between font-mono text-[10px] text-white/45"><span data-testid="stems-time">{fmtClock(p.time)}</span><span>{fmtClock(p.duration || set.duration)}</span></div>
        </div>
        <button type="button" data-testid="stems-reset-mix" onClick={p.reset} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/50 hover:text-white">
          <RotateCcw className="h-3.5 w-3.5" /> Reset levels
        </button>
      </div>
      <div className="space-y-2">
        {set.stems.map((s) => (
          <StemRow key={s.name} stem={s} mix={p.mix[s.name]} onChange={(patch) => p.update(s.name, patch)} audible={anySolo ? p.mix[s.name].solo : !p.mix[s.name].mute} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4">
        <p className="mr-auto font-display text-[15px] font-semibold">Download every part</p>
        <a href={stemsZipUrl(set.id, "wav")} download data-testid="stems-download-all-wav" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 px-5 py-2.5 font-display text-sm font-semibold text-[#05070f]">
          <Download className="h-4 w-4" /> All as WAV <span className="font-mono text-[10px] opacity-70">{fmtBytes(total("wav_bytes"))}</span>
        </a>
        <a href={stemsZipUrl(set.id, "mp3")} download data-testid="stems-download-all-mp3" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-sm text-white/85 hover:bg-white/[0.06]">
          <Download className="h-4 w-4" /> All as MP3 <span className="font-mono text-[10px] text-white/45">{fmtBytes(total("mp3_bytes"))}</span>
        </a>
        <p className="w-full text-[11px] text-white/40">WAV = full studio quality (16-bit, 44.1 kHz) for mixing engineers · MP3 = 320 kbps, smaller files for sharing.</p>
      </div>
    </section>
  );
}
